import version from "version";
import Variables from "./variables";
import Request from "./request";
import API from "./api";
import Interface from "./interface";
import Common from "./common";

const FCA_EVENT = /^fca\./;
const pendingPromises = {};
const ids = {};

export default class FacilioApp {
  constructor(options) {
    this._origin = options.origin;
    this._source = window.parent;
    this._cappId = options.cappId;
    this._context = null;
    this._widget = null;
    this._isExportMode = false;
    this._currentUser = null;
    this._currentOrg = null;
    this._messageHandlers = {};

    this.common = new Common(this);
    this.variables = new Variables(this);
    this.request = new Request(this);
    this.api = new API(this);
    this.interface = new Interface(this);

    this.ready = false;

    this.on(
      "app.loaded",
      (data) => {
        this.ready = true;
        this._context = data.context;
        this._widget = data.widget;
        this._isExportMode = data.exportMode;
        this._currentUser = data.currentUser;
        this._currentOrg = data.currentOrg;
      },
      this
    );

    window.addEventListener("message", messageHandler.bind(null, this));
    this._postMessage("app.handshake", { version: version });
  }

  on(name, handler, context) {
    if (typeof handler === "function") {
      handler = context ? handler.bind(context) : handler;

      this._messageHandlers[name] = this._messageHandlers[name] || [];
      this._messageHandlers[name].push(handler);
    }
  }

  off(name, handler) {
    if (!this._messageHandlers[name]) {
      return false;
    }
    const index = this._messageHandlers[name].indexOf(handler);
    if (this.has(name, handler)) {
      this._messageHandlers[name].splice(index, 1);
    }
    return handler;
  }

  has(name, handler) {
    if (!this._messageHandlers[name]) {
      return false;
    }
    return this._messageHandlers[name].indexOf(handler) !== -1;
  }

  getContext() {
    return this._context;
  }

  getWidget() {
    return this._widget;
  }

  isExportMode() {
    return this._isExportMode;
  }

  getCurrentUser() {
    return this._currentUser;
  }

  getCurrentOrg() {
    return this._currentOrg;
  }

  _nextIdFor(name) {
    if (isNaN(ids[name])) {
      ids[name] = 0;
    }
    return ++ids[name];
  }

  _postMessageWithPromise(key, params) {
    const id = this._nextIdFor("promise");
    let timeoutId, promiseStartTime;
    const client = this;

    const promise = new Promise((resolve, reject) => {
      promiseStartTime = window.performance.now();

      pendingPromises[id] = { resolve, reject };

      const msg = JSON.stringify({
        id: id,
        key: key,
        params: params,
        instanceUid: this._cappId,
      });
      this._source.postMessage(msg, this._origin);
    });
    return promise
  }

  _postMessage(name, data) {
    const msg = JSON.stringify({
      key: name,
      message: data,
      instanceUid: this._cappId,
    });
    this._source.postMessage(msg, this._origin);
  }
}

function isValidEvent(client, event) {
  return (
    client && client._origin === event.origin && client._source === event.source
  );
}

function triggerEvent(client, eventName, data) {
  if (!client._messageHandlers[eventName]) {
    return false;
  }
  client._messageHandlers[eventName].forEach((handler) => {
    handler(data);
  });
}

function finalizePendingPromise(pendingPromise, data) {
  if (data.error) {
    const error = typeof (data.error) === 'object' ? new Error(data.error.message) : new Error(data.error)
    pendingPromise.reject(error);
  } else {
    pendingPromise.resolve(data.result);
  }

  delete pendingPromises[data.id]
}

function messageHandler(client, event) {
  if (!isValidEvent(client, event)) {
    return;
  }

  let data = event.data;

  if (!data) {
    return;
  }

  if (typeof data === "string") {
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      return e;
    }
  }

  let pendingPromise;

  if (data.id && (pendingPromise = pendingPromises[data.id])) {
    finalizePendingPromise(pendingPromise, data);
  } else if (FCA_EVENT.test(data.key)) {
    const key = data.key.replace(FCA_EVENT, "");

    triggerEvent(client, key, data.message);
  }
}
